import { createFileRoute, useSearch, useRouter } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { graphql, useMutation } from "react-relay";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { FieldError, Field, FieldLabel } from "@/components/ui/field";
import { Heading } from "@/components/heading";
import { TextLink, Text } from "@/components/text";
import { useForm } from "@tanstack/react-form";
import { Spinner } from "@/components/ui/spinner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useRouteContext } from "@tanstack/react-router";
import { Icon } from "@/components/icon";
import type { redefinirSenhaMutation } from "./__generated__/redefinirSenhaMutation.graphql";
import { useState } from "react";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from "@/components/ui/input-group";

export const Route = createFileRoute("/_auth/redefinir-senha")({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			recovery_token: z.string(),
		}),
	),
});

function RouteComponent() {
	const { recovery_token: token } = useSearch({ from: Route.id });
	const { club } = useRouteContext({ from: "__root__" });
	const router = useRouter();

	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
		useState(false);

	const [confirmPasswordReset] = useMutation<redefinirSenhaMutation>(graphql`
		mutation redefinirSenhaMutation($payload: ConfirmPasswordResetInput!) {
			confirmPasswordReset(payload: $payload)
		}
`);

	const form = useForm({
		defaultValues: {
			password: "",
			confirmPassword: "",
		},
		validators: {
			onChangeAsyncDebounceMs: 600,
			onChangeAsync: z
				.object({
					password: z.string().min(1, "A senha é obrigatória"),
					confirmPassword: z.string().min(1, "Confirme sua senha"),
				})
				.superRefine((data, ctx) => {
					if (data.password.length < 8) {
						ctx.addIssue({
							code: "custom",
							message: "A senha precisa ter no mínimo 8 caracteres",
							path: ["password"],
							input: data.password,
						});
					}

					// validate if password contains at least one uppercase letter
					if (!/[A-Z]/.test(data.password)) {
						ctx.addIssue({
							code: "custom",
							message: "A senha precisa conter pelo menos uma letra maiúscula",
							path: ["password"],
							input: data.password,
						});
					}

					// validate if password contains at least one lowercase letter
					if (!/[a-z]/.test(data.password)) {
						ctx.addIssue({
							code: "custom",
							message: "A senha precisa conter pelo menos uma letra minúscula",
							path: ["password"],
							input: data.password,
						});
					}

					// validate if password contains at least one number
					if (!/\d/.test(data.password)) {
						ctx.addIssue({
							code: "custom",
							message: "A senha precisa conter pelo menos um número",
							path: ["password"],
							input: data.password,
						});
					}

					// validate if password contains at least one special character
					if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(data.password)) {
						ctx.addIssue({
							code: "custom",
							message:
								"A senha precisa conter pelo menos um caractere especial",
							path: ["password"],
							input: data.password,
						});
					}

					if (data.password !== data.confirmPassword) {
						ctx.addIssue({
							code: "custom",
							message: "As senhas não coincidem",
							path: ["confirmPassword"],
							input: data.password,
						});
					}
				}),
		},
		onSubmit: async ({ value }) => {
			return confirmPasswordReset({
				variables: {
					payload: {
						code: token,
						password: value.password!,
					},
				},
				onCompleted: (response) => {
					if (response.confirmPasswordReset) {
						toast.success("Senha redefinida com sucesso!");
						setTimeout(() => {
							router.navigate({
								to: "/login",
							});
						}, 2000);
					}
				},
				onError: (err) => {
					if (
						err.message?.includes("invalid") ||
						err.message?.includes("expired")
					) {
						toast.error(
							"Token inválido ou expirado. Solicite uma nova recuperação de senha.",
						);
						setTimeout(() => {
							router.navigate({
								to: "/esqueci-a-senha",
							});
						}, 3000);
					} else {
						toast.error(
							"Não foi possível redefinir sua senha. Tente novamente.",
						);
					}
				},
			});
		},
	});

	return (
		<div className="flex justify-center w-full">
			<div className="flex w-full flex-col gap-8 justify-center p-6 max-w-[550px]">
				<div className="size-12">
					<Avatar>
						<AvatarFallback>
							{club?.name.slice(0, 2).toUpperCase()}
						</AvatarFallback>
						<AvatarImage src={club?.logo?.signedUrl} />
					</Avatar>
				</div>

				<Heading>Redefinir Senha</Heading>

				<Text className="text-left text-muted-foreground">
					Digite sua nova senha abaixo para redefinir o acesso à sua conta.
				</Text>

				<form
					className="flex flex-col gap-8"
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
					<form.Field name="password">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Nova Senha</FieldLabel>
								<InputGroup>
									<InputGroupInput
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										type={isPasswordVisible ? "text" : "password"}
										placeholder="Digite sua nova senha"
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>

									<InputGroupAddon align="inline-end">
										<InputGroupButton
											type="button"
											onClick={() => setIsPasswordVisible((p) => !p)}
											tabIndex={-1}
										>
											<Icon name={isPasswordVisible ? "EyeOff" : "Eye"} />
										</InputGroupButton>
									</InputGroupAddon>
								</InputGroup>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Field name="confirmPassword">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>
									Confirmar Nova Senha
								</FieldLabel>
								<InputGroup>
									<InputGroupInput
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										type={isConfirmPasswordVisible ? "text" : "password"}
										placeholder="Confirme sua nova senha"
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>

									<InputGroupAddon align="inline-end">
										<InputGroupButton
											type="button"
											onClick={() => setIsConfirmPasswordVisible((p) => !p)}
											tabIndex={-1}
										>
											<Icon
												name={isConfirmPasswordVisible ? "EyeOff" : "Eye"}
											/>
										</InputGroupButton>
									</InputGroupAddon>
								</InputGroup>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.isSubmitSuccessful]}
					>
						{([isSubmitting, isSubmitSuccessful]) => (
							<div className="flex flex-col gap-6">
								<Button
									type="submit"
									color={isSubmitSuccessful ? "emerald" : "primary"}
									disabled={isSubmitting || isSubmitSuccessful}
									className="flex items-center justify-center gap-2"
								>
									{isSubmitSuccessful ? (
										<>
											Senha redefinida!{" "}
											<Icon name="CircleCheck" className="size-4" />
										</>
									) : (
										<>Redefinir senha {isSubmitting ? <Spinner /> : null}</>
									)}
								</Button>

								<Text className="font-medium">
									Lembrou sua senha?{" "}
									<TextLink to="/login" disabled={isSubmitting}>
										Voltar para login
									</TextLink>
								</Text>
							</div>
						)}
					</form.Subscribe>
				</form>
			</div>
		</div>
	);
}
